import type { CSSProperties, ReactNode } from 'react';

import type { MainActionButtonType } from '../types/MainActionButtonViewModel';
import { MainActionButtonIcon } from './MainActionButtonIcon';

/**
 * Shared root class for a main action button — used by the server-rendered
 * {@link MainActionLink} `<a>` and the client Message Me `<button>` island so
 * both surfaces render identically. `flex-1` shares the row evenly via its gap.
 */
export const kMainActionButtonClassName =
  'flex min-h-10 min-w-0 flex-1 items-center justify-center gap-[5px] rounded-md px-4';

/** The fully-resolved inline style shared by every main action button surface. */
export function mainActionButtonInlineStyle(params: {
  backgroundColor: string;
  textColor: string;
  textStyle: CSSProperties;
}): CSSProperties {
  return {
    ...params.textStyle,
    backgroundColor: params.backgroundColor,
    color: params.textColor,
  };
}

/** Icon + truncated label — the inner content shared by both button surfaces.
 *  `labelNode` (used only by the follow-aware subscribe label) renders in place
 *  of the static `label` string. */
export function MainActionButtonContent({
  type,
  label,
  labelNode,
  textColor,
  leftIcon,
}: {
  type: MainActionButtonType;
  label: string;
  labelNode?: ReactNode;
  textColor: string;
  leftIcon?: ReactNode;
}) {
  return (
    <>
      {leftIcon ?? <MainActionButtonIcon type={type} color={textColor} />}
      <span className="truncate">{labelNode ?? label}</span>
    </>
  );
}
